import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import MetricasProceso from './MetricasProceso';
import ResumenEstadisticas from './ResumenEstadisticas';
import TimeLineChart from './TimeLineChart';
import ProcessEfficiencyChart from './ProcessEfficiencyChart';
import { useEstadisticasOrden } from '../../hooks/useEstadisticasOrden';

const EstadisticasOrdenModal = ({
  isOpen,
  onClose,
  ordenId,
  ordenData
}) => {
  // Estado para las pestañas
  const [activeTab, setActiveTab] = useState('resumen');
  
  // Hook para obtener estadísticas
  const { procesos, resumen, loading, error, recargarEstadisticas } = useEstadisticasOrden(
    ordenId, 
    isOpen // Solo cargar cuando el modal esté abierto
  );

  // Manejar tecla Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event) => {
    // Solo cerrar si se hace clic en el backdrop, no en el contenido del modal
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (event) => {
    // Prevenir que el clic en el contenido del modal cierre el modal
    event.stopPropagation();
  };

  const formatOrderNumber = (numero) => {
    return numero ? `#${numero}` : `#${ordenId}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      backdrop="static"
      keyboard={true}
      onClick={handleBackdropClick}
      data-testid="estadisticas-modal"
    >
      <ModalHeader toggle={onClose}>
        <i className="fas fa-chart-line me-2" />
        Estadísticas de Orden {formatOrderNumber(ordenData?.numero)}
      </ModalHeader>
      
      <ModalBody data-testid="modal-content" onClick={handleModalClick}>
        {loading && (
          <div className="text-center py-4" data-testid="loading-spinner">
            <Spinner color="primary" />
            <p className="mt-2">Cargando estadísticas...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" data-testid="error-alert">
            <i className="fas fa-exclamation-triangle me-2" />
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Navegación por pestañas */}
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={activeTab === 'resumen' ? 'active' : ''}
                  onClick={() => setActiveTab('resumen')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-info-circle me-1" />
                  Resumen
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'procesos' ? 'active' : ''}
                  onClick={() => setActiveTab('procesos')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-cogs me-1" />
                  Procesos
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'timeline' ? 'active' : ''}
                  onClick={() => setActiveTab('timeline')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-chart-line me-1" />
                  Timeline
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'eficiencia' ? 'active' : ''}
                  onClick={() => setActiveTab('eficiencia')}
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-chart-pie me-1" />
                  Eficiencia
                </NavLink>
              </NavItem>
            </Nav>

            {/* Contenido de las pestañas */}
            <TabContent activeTab={activeTab}>
              {/* Pestaña Resumen */}
              <TabPane tabId="resumen">
                <div className="mb-4">
                  <h5 className="mb-3">
                    <i className="fas fa-info-circle me-2" />
                    Resumen General
                  </h5>
                  <ResumenEstadisticas resumen={resumen} />
                </div>
              </TabPane>

              {/* Pestaña Procesos */}
              <TabPane tabId="procesos">
                <div>
                  <h5 className="mb-3">
                    <i className="fas fa-cogs me-2" />
                    Detalle por Proceso
                  </h5>
                  <MetricasProceso procesos={procesos} />
                </div>
              </TabPane>

              {/* Pestaña Timeline */}
              <TabPane tabId="timeline">
                <div>
                  <h5 className="mb-3">
                    <i className="fas fa-chart-line me-2" />
                    Timeline de Procesos
                  </h5>
                  <TimeLineChart procesos={procesos} />
                </div>
              </TabPane>

              {/* Pestaña Eficiencia */}
              <TabPane tabId="eficiencia">
                <div>
                  <h5 className="mb-3">
                    <i className="fas fa-chart-pie me-2" />
                    Distribución de Tiempo
                  </h5>
                  <ProcessEfficiencyChart procesos={procesos} />
                </div>
              </TabPane>
            </TabContent>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          <i className="fas fa-times me-1" />
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

EstadisticasOrdenModal.propTypes = {
  /** Estado de apertura del modal */
  isOpen: PropTypes.bool.isRequired,
  /** Función callback para cerrar el modal */
  onClose: PropTypes.func.isRequired,
  /** ID de la orden de producción */
  ordenId: PropTypes.string.isRequired,
  /** Datos de la orden de producción */
  ordenData: PropTypes.shape({
    id: PropTypes.string,
    numero: PropTypes.number,
    fecha_creacion: PropTypes.string,
    fecha_inicio: PropTypes.string,
    fecha_finalizacion: PropTypes.string
  })
};

EstadisticasOrdenModal.defaultProps = {
  ordenData: null
};

export default EstadisticasOrdenModal;
